import { useToast } from "@chakra-ui/react";
import { useFlags } from "launchdarkly-react-client-sdk";

import { PaymentInfoCard } from "@/components/payment/PaymentInfoCard";
import { WorkflowButton } from "@/components/workflow/WorkflowButton";
import { stripeAccountPromise, stripePromise } from "@/helpers/stripe";
import {
  useAcknowledgeWirePaymentMutation,
  useInitiatePaymentMutation,
  useInitiateWirePaymentMutation,
  useInvestment,
  useSetupManualInvestmentPayment,
} from "@/hooks/useInvestments";
import { useOfferingAnalytics } from "@/hooks/useOfferingAnalytics";
import { useOfferingV2 } from "@/hooks/useOfferings";
import { extractErrorMessage } from "@equidefi/portals";
import { HighlightCard } from "@equidefi/portals/components/investments/HighlightCard";
import { CurrencyFormatter } from "@equidefi/portals/helpers/format";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { canEditPaymentMethod, isPaymentFailed, latestInvestorPayment } from "@equidefi/shared";
import { Heading } from "@equidefi/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { WorkflowContainer } from "../../workflow/WorkflowContainer";
import { PaymentAccordion } from "../../workflow/payment/PaymentAccordion";
import { PaymentStepContext } from "../../workflow/payment/context";

export const STRIPE_ACCORDION_TYPES = {
  STRIPE: "STRIPE",
  STRIPE_WIRE: "STRIPE_WIRE",
};

// A closed/canceled investment is fully settled out — it can no longer add or
// switch a payment method, so we bounce the investor back to the detail page.
const TERMINAL_INVESTMENT_STATUSES = ["CLOSED", "CANCELED"];

const VaultPayment = () => {
  const { id } = useParams();

  const { data: investment, refetch } = useInvestment(id, {
    refetchOnMount: true,
  });
  const { data: offering } = useOfferingV2(investment?.offering?.id);

  const navigate = useNavigate();
  const analytics = useOfferingAnalytics(offering, investment);

  const isInvestmentClosed = investment ? TERMINAL_INVESTMENT_STATUSES.includes(investment.status) : false;

  useEffect(() => {
    if (isInvestmentClosed) {
      navigate(`/vault/investment/${id}`, { replace: true });
    }
  }, [isInvestmentClosed, id, navigate]);

  const setupManualPayment = useSetupManualInvestmentPayment(id);

  const settingsMap = useMemo(() => {
    const transferAgent = offering?.offering_contacts?.find((c) => c.type === "TRANSFER_AGENT");

    return {
      bank_name: offering?.payment_bank_name,
      account_name: offering?.payment_account_name,
      account_number: offering?.payment_account_number,
      routing_number: offering?.payment_routing_number,
      transfer_agent_name: transferAgent?.company,
      wireInstructions: offering?.manual_payment_wire_instructions,
      checkInstructions: offering?.manual_payment_check_instructions,
    };
  }, [offering]);

  const createPayment = useInitiatePaymentMutation(id);
  const createWirePayment = useInitiateWirePaymentMutation(id);
  const acknowledgeWirePayment = useAcknowledgeWirePaymentMutation(id);

  useDocumentTitle(["Payment", investment?.offering?.name, investment?.issuer?.name]);

  const [paymentType, setPaymentType] = useState(null);

  const { stripeDeferred } = useFlags();

  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [wireInstructions, setWireInstructions] = useState(null);
  const [wirePaymentIntentId, setWirePaymentIntentId] = useState("");
  const [stripe, setStripe] = useState(null);
  const toast = useToast();

  const ensureStripeLoaded = () => {
    if (stripe) return;

    if (offering.stripe_account_id) {
      setStripe(stripeAccountPromise(offering.stripe_account_id));
    } else {
      setStripe(stripePromise);
    }
  };

  const handleSectionChange = (type) => {
    setPaymentType(type);
    if (type === STRIPE_ACCORDION_TYPES.STRIPE && stripeDeferred) ensureStripeLoaded();
    if (type === STRIPE_ACCORDION_TYPES.STRIPE && !stripeDeferred) setupStripePayment();
    if (type === STRIPE_ACCORDION_TYPES.STRIPE_WIRE) setupStripeWirePayment();
  };

  const setupStripePayment = async () => {
    if (clientSecret) return null;

    try {
      const clientSecretData = await createPayment.mutateAsync();
      ensureStripeLoaded();
      setClientSecret(clientSecretData.clientSecret);
      setPaymentIntentId(clientSecretData.id);
    } catch (e) {
      console.error(e);
    }
  };

  const setupStripeWirePayment = async () => {
    if (wireInstructions) return null;

    try {
      const result = await createWirePayment.mutateAsync();
      setWireInstructions(result?.wireInstructions ?? null);
      setWirePaymentIntentId(result?.id ?? "");
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: "Could not generate wire instructions.",
      });
    }
  };

  const handleWireContinue = async () => {
    if (!wirePaymentIntentId) {
      navigate(`/vault/investment/${investment.id}`);
      return;
    }
    try {
      await acknowledgeWirePayment.mutateAsync(wirePaymentIntentId);
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: extractErrorMessage(e, "Could not submit wire payment. Please try again."),
      });
      return;
    }
    navigate(`/vault/investment/${investment.id}`);
  };

  const handlePayment = useCallback(async () => {
    analytics.track("payment", {
      amount: investment.amount,
      type: paymentType.toLowerCase(),
    });

    try {
      await setupManualPayment.mutateAsync({
        category: "MANUAL",
        payment_type: paymentType.toLowerCase(),
      });

      toast({
        status: "success",
        description: "Successfully updated payment method",
      });

      navigate(`/vault/investment/${investment.id}`);
    } catch (e) {
      console.error(e);
      const message = extractErrorMessage(e, "Failed to update payment method. Please try again.");
      toast({ status: "error", description: message });
    }
  }, [analytics, investment?.amount, setupManualPayment.mutateAsync, paymentType, toast, investment?.id, navigate]);

  // `ensureStripeLoaded` reads `offering.stripe_account_id` synchronously when the
  // accordion opens, and the wrong account would render the wrong payment methods.
  if (!investment || !offering || isInvestmentClosed) return null;

  const latestPayment = latestInvestorPayment(investment.payments);
  const canEditMethod = canEditPaymentMethod(investment);

  return (
    <PaymentStepContext.Provider
      value={{
        paymentIntentId,
        clientSecret,
      }}>
      <WorkflowContainer title="Payment">
        {Boolean(latestPayment) && (isPaymentFailed(latestPayment) || !canEditMethod) ? (
          <PaymentInfoCard payment={latestPayment} mt={6} mb="4" />
        ) : (
          <HighlightCard text={CurrencyFormatter.format(investment.amount)} title="Total Due" />
        )}

        {canEditMethod ? (
          <>
            <Heading fontWeight="bold" textStyle="h2" mt={6} mb={4}>
              Select Your Payment Method
            </Heading>

            <PaymentAccordion
              onSubmitManualPayment={handlePayment}
              onSectionChange={handleSectionChange}
              onWireContinue={handleWireContinue}
              wireInstructions={wireInstructions}
              isWireLoading={createWirePayment.isLoading}
              isWireContinueLoading={acknowledgeWirePayment.isLoading}
              stripe={stripe}
              clientSecret={clientSecret}
              isManualPaymentLoading={setupManualPayment.isLoading}
              paymentSettings={settingsMap}
              investment={investment}
              offering={offering}
              deferredIntent={stripeDeferred}
              refetchInvestment={refetch}
              analytics={analytics}
              redirectUrl={`/vault/investment/${investment?.id}`}
            />
          </>
        ) : (
          <WorkflowButton mt={6} onClick={() => navigate(`/vault/investment/${investment.id}`)}>
            Back to Investment
          </WorkflowButton>
        )}
      </WorkflowContainer>
    </PaymentStepContext.Provider>
  );
};

export default VaultPayment;
